import React, { useState } from 'react';
import {
  Layers,
  Search,
  BookOpen,
  GitBranch,
  Terminal,
  Cpu,
  Database,
  Shield,
  Zap,
  CheckCircle,
  AlertTriangle,
  Play,
  Copy,
  Check,
  Code2,
  Share2,
  FolderGit2,
  FileText,
  DollarSign,
  Radio,
  ArrowRight,
  Sparkles,
  RefreshCw,
  Box,
  Compass
} from 'lucide-react';
import { AppView } from '../../types';
import {
  MOCK_CATALOG_ENTITIES,
  MOCK_GOLDEN_PATHS,
  MOCK_PLATFORM_OFFERINGS,
  MOCK_PROVISIONING_REQUESTS,
  MOCK_TECH_RADAR,
  MOCK_ENGINEERING_STANDARDS,
  MOCK_DEPENDENCIES,
  computeImpactAnalysis,
  generateCatalogYaml
} from '../../services/platform/platformStore';
import {
  SoftwareCatalogEntity,
  GoldenPathDefinition,
  ArchitectureImpactAnalysis
} from '../../services/platform/platformTypes';

interface EngineeringPortalProps {
  onNavigateToView?: (view: AppView) => void;
}

export const EngineeringPortal: React.FC<EngineeringPortalProps> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<'catalog' | 'golden-paths' | 'self-service' | 'blast-radius' | 'radar' | 'standards' | 'docs'>('catalog');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDomain, setFilterDomain] = useState<string>('ALL');
  const [filterCriticality, setFilterCriticality] = useState<string>('ALL');
  const [selectedEntity, setSelectedEntity] = useState<SoftwareCatalogEntity | null>(MOCK_CATALOG_ENTITIES[0]);
  const [copiedYaml, setCopiedYaml] = useState(false);

  // Golden Path Runner State
  const [selectedGoldenPath, setSelectedGoldenPath] = useState<GoldenPathDefinition>(MOCK_GOLDEN_PATHS[0]);
  const [runningGp, setRunningGp] = useState(false);
  const [gpStepIndex, setGpStepIndex] = useState(0);
  const [gpLogs, setGpLogs] = useState<string[]>([]);
  const [gpComplete, setGpComplete] = useState(false);

  // Blast Radius Analyzer State
  const [impactTargetId, setImpactTargetId] = useState<string>(MOCK_CATALOG_ENTITIES[0].entityId);
  const [impactChangeType, setImpactChangeType] = useState<ArchitectureImpactAnalysis['changeType']>('DATABASE_SCHEMA');
  const [impactResult, setImpactResult] = useState<ArchitectureImpactAnalysis>(
    computeImpactAnalysis(MOCK_CATALOG_ENTITIES[0].entityId, 'DATABASE_SCHEMA')
  );

  // Self Service State
  const [selectedOfferingId, setSelectedOfferingId] = useState(MOCK_PLATFORM_OFFERINGS[0].id);
  const [offeringInputs, setOfferingInputs] = useState<Record<string, any>>({
    namespaceName: 'loan_quote_cache:',
    defaultTtlSeconds: 1800,
    memoryBudgetMb: 32
  });
  const [submittingReq, setSubmittingReq] = useState(false);
  const [reqSuccessMsg, setReqSuccessMsg] = useState<string | null>(null);

  // Filter Catalog
  const filteredEntities = MOCK_CATALOG_ENTITIES.filter((e) => {
    const matchesSearch =
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.entityId.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDomain = filterDomain === 'ALL' || e.domain === filterDomain;
    const matchesCriticality = filterCriticality === 'ALL' || e.criticality === filterCriticality;
    return matchesSearch && matchesDomain && matchesCriticality;
  });

  const domains = Array.from(new Set(MOCK_CATALOG_ENTITIES.map((e) => e.domain)));

  const handleCopyYaml = (entity: SoftwareCatalogEntity) => {
    const yaml = generateCatalogYaml(entity);
    navigator.clipboard.writeText(yaml);
    setCopiedYaml(true);
    setTimeout(() => setCopiedYaml(false), 2000);
  };

  const handleRunGoldenPath = () => {
    setRunningGp(true);
    setGpStepIndex(0);
    setGpComplete(false);
    setGpLogs([
      `[${new Date().toLocaleTimeString()}] Initializing Golden Path: ${selectedGoldenPath.name} (v${selectedGoldenPath.version})...`,
      `[${new Date().toLocaleTimeString()}] Validating monorepo workspace & strict TypeScript constraints...`
    ]);

    const totalSteps = selectedGoldenPath.steps.length;
    let current = 0;

    const interval = setInterval(() => {
      current += 1;
      if (current < totalSteps) {
        setGpStepIndex(current);
        const step = selectedGoldenPath.steps[current];
        setGpLogs((prev) => [
          ...prev,
          `[${new Date().toLocaleTimeString()}] Step ${step.order}: ${step.title} -> ${step.description}`,
          `[${new Date().toLocaleTimeString()}] Executing automated validation hooks & lint checks...`
        ]);
      } else {
        clearInterval(interval);
        setRunningGp(false);
        setGpComplete(true);
        setGpLogs((prev) => [
          ...prev,
          `[${new Date().toLocaleTimeString()}] ✅ Golden Path execution succeeded!`,
          `[${new Date().toLocaleTimeString()}] Registered in Software Catalog with catalog.yaml descriptor.`,
          `[${new Date().toLocaleTimeString()}] Ready for review & PR merge.`
        ]);
      }
    }, 1200);
  };

  const handleAnalyzeImpact = () => {
    const res = computeImpactAnalysis(impactTargetId, impactChangeType);
    setImpactResult(res);
  };

  const handleSubmitSelfService = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingReq(true);
    setReqSuccessMsg(null);
    setTimeout(() => {
      setSubmittingReq(false);
      setReqSuccessMsg(`Request REQ-${Math.floor(1000 + Math.random() * 9000)} created successfully and queued for instant provisioning.`);
    }, 900);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans pb-20">
      {/* Top Header */}
      <div className="bg-white border-b border-[#E2E8F0] sticky top-0 z-30 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-xs">
                <Layers className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-black tracking-tight text-[#0F172A]">YamooPay Developer Portal (IDP)</h1>
                  <span className="text-[10px] font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full uppercase tracking-wider">
                    Engineering OS
                  </span>
                </div>
                <p className="text-xs text-[#64748B]">
                  Software Catalog, Catalog-as-Code, Golden Paths, Self-Service Datastores, & Blast Radius Engine
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onNavigateToView?.('cto-command-center')}
                className="px-3.5 py-2 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Compass className="w-4 h-4 text-indigo-600" />
                <span>CTO Command Center</span>
              </button>
              <button
                onClick={() => onNavigateToView?.('developer-portal')}
                className="px-3.5 py-2 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <Code2 className="w-4 h-4 text-blue-600" />
                <span>Public API Docs</span>
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center gap-1 mt-4 overflow-x-auto no-scrollbar border-t border-slate-100 pt-2">
            {[
              { id: 'catalog', label: 'Software Catalog', icon: Box },
              { id: 'golden-paths', label: 'Golden Paths', icon: Zap },
              { id: 'self-service', label: 'Self-Service Catalog', icon: Cpu },
              { id: 'blast-radius', label: 'Blast Radius & Impact', icon: Share2 },
              { id: 'radar', label: 'Technology Radar', icon: Radio },
              { id: 'standards', label: 'Engineering Standards', icon: Shield },
              { id: 'docs', label: 'Docs & Onboarding', icon: BookOpen }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-3 py-2 text-xs font-bold rounded-lg flex items-center gap-2 whitespace-nowrap transition-all cursor-pointer ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* TAB 1: SOFTWARE CATALOG */}
        {activeTab === 'catalog' && (
          <div className="space-y-6">
            {/* Filters Bar */}
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 shadow-2xs flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="relative w-full md:w-80">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search service, module, domain, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-bold text-slate-500">Domain:</span>
                  <select
                    value={filterDomain}
                    onChange={(e) => setFilterDomain(e.target.value)}
                    className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 font-medium text-slate-700 focus:outline-hidden"
                  >
                    <option value="ALL">All Domains ({MOCK_CATALOG_ENTITIES.length})</option>
                    {domains.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-bold text-slate-500">Criticality:</span>
                  <select
                    value={filterCriticality}
                    onChange={(e) => setFilterCriticality(e.target.value)}
                    className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 font-medium text-slate-700 focus:outline-hidden"
                  >
                    <option value="ALL">All Criticality</option>
                    <option value="TIER_0">Tier 0 (Critical Core)</option>
                    <option value="TIER_1">Tier 1 (Customer Facing)</option>
                    <option value="TIER_2">Tier 2 (Important)</option>
                    <option value="TIER_3">Tier 3 (Internal)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Catalog Grid & Inspector */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: Entity Cards */}
              <div className="lg:col-span-7 space-y-3">
                <div className="flex items-center justify-between text-xs text-slate-500 font-bold px-1">
                  <span>Showing {filteredEntities.length} catalog entities</span>
                  <span className="text-emerald-700 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> 100% Owned & Documented
                  </span>
                </div>

                {filteredEntities.map((entity) => {
                  const isSelected = selectedEntity?.entityId === entity.entityId;
                  return (
                    <div
                      key={entity.entityId}
                      onClick={() => setSelectedEntity(entity)}
                      className={`bg-white border rounded-xl p-4 transition-all cursor-pointer shadow-2xs hover:shadow-xs ${
                        isSelected
                          ? 'border-blue-600 ring-2 ring-blue-500/20 bg-blue-50/20'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                              {entity.kind}
                            </span>
                            <h3 className="text-sm font-bold text-slate-900">{entity.name}</h3>
                          </div>
                          <p className="text-xs text-slate-600 mt-1 line-clamp-2 leading-relaxed">
                            {entity.description}
                          </p>
                        </div>

                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span
                            className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                              entity.criticality === 'TIER_0'
                                ? 'bg-red-100 text-red-800 border border-red-200'
                                : 'bg-amber-100 text-amber-800 border border-amber-200'
                            }`}
                          >
                            {entity.criticality}
                          </span>
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                            {entity.lifecycle}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-slate-700">{entity.ownership.ownerTeamName}</span>
                          <span>•</span>
                          <span>{entity.ownership.technicalLead}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-slate-600">SLO {entity.slo.availabilityTarget}</span>
                          <span className="text-slate-300">|</span>
                          <span className="font-bold text-slate-700">${entity.allocatedMonthlyCostUsd}/mo</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right Column: Entity Inspector Drawer */}
              <div className="lg:col-span-5">
                {selectedEntity ? (
                  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs sticky top-28 space-y-5">
                    <div className="flex items-start justify-between gap-3 pb-4 border-b border-slate-100">
                      <div>
                        <span className="text-[10px] font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                          {selectedEntity.entityId}
                        </span>
                        <h2 className="text-base font-black text-slate-900 mt-1">{selectedEntity.name}</h2>
                        <p className="text-xs text-slate-500">{selectedEntity.domain} • {selectedEntity.system}</p>
                      </div>

                      <button
                        onClick={() => handleCopyYaml(selectedEntity)}
                        className="px-2.5 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center gap-1 transition-colors cursor-pointer"
                        title="Copy catalog.yaml representation"
                      >
                        {copiedYaml ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        <span>{copiedYaml ? 'Copied' : 'YAML'}</span>
                      </button>
                    </div>

                    {/* Quick Specs Grid */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Primary Owner</span>
                        <div className="font-bold text-slate-800 mt-0.5">{selectedEntity.ownership.technicalLead}</div>
                        <div className="text-[11px] text-slate-500">{selectedEntity.ownership.ownerTeamName}</div>
                      </div>

                      <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Runtime & VPS</span>
                        <div className="font-bold text-slate-800 mt-0.5">{selectedEntity.runtime.language}</div>
                        <div className="text-[11px] text-slate-500 truncate">{selectedEntity.runtime.deploymentUnit}</div>
                      </div>

                      <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Service Level (SLO)</span>
                        <div className="font-bold text-emerald-700 mt-0.5">{selectedEntity.slo.availabilityTarget}</div>
                        <div className="text-[11px] text-slate-500">Latency P99: {selectedEntity.slo.latencyP99Target}</div>
                      </div>

                      <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">FinOps Cost</span>
                        <div className="font-bold text-slate-900 mt-0.5">${selectedEntity.allocatedMonthlyCostUsd} / mo</div>
                        <div className="text-[11px] text-slate-500">{selectedEntity.costCenter}</div>
                      </div>
                    </div>

                    {/* Operational Runbook Link */}
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <div>
                          <div className="text-xs font-bold text-blue-900">Operational Runbook</div>
                          <div className="text-[10px] text-blue-700 font-mono">Verified {selectedEntity.documentation.lastReviewedAt}</div>
                        </div>
                      </div>
                      <a
                        href={selectedEntity.documentation.runbookUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-bold text-blue-700 hover:text-blue-900 underline"
                      >
                        Open Runbook
                      </a>
                    </div>

                    {/* Declarative Catalog-as-Code Preview */}
                    <div>
                      <div className="flex items-center justify-between text-xs font-bold text-slate-700 mb-1.5">
                        <span className="flex items-center gap-1.5">
                          <Code2 className="w-3.5 h-3.5 text-slate-500" />
                          <span>catalog.yaml (Catalog-as-Code)</span>
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">RFC-PLAT-008</span>
                      </div>
                      <pre className="bg-slate-900 text-slate-200 text-[11px] font-mono p-3 rounded-lg overflow-x-auto max-h-56 leading-relaxed">
                        {generateCatalogYaml(selectedEntity)}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-400">
                    Select a catalog entity to inspect details and metadata.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: GOLDEN PATHS */}
        {activeTab === 'golden-paths' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-2 text-blue-300 text-xs font-bold uppercase tracking-wider mb-2">
                <Zap className="w-4 h-4" />
                <span>YamooPay Golden Path Platform</span>
              </div>
              <h2 className="text-xl font-black">Standardized, Automated & Guardrailed Developer Workflows</h2>
              <p className="text-xs text-blue-100 mt-1 max-w-2xl leading-relaxed">
                Golden Paths represent the supported, best-practice route to build new capabilities without repetitive boilerplate, manual tickets, or architectural drift.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column: Golden Path Selection */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1">Available Golden Paths</h3>
                {MOCK_GOLDEN_PATHS.map((gp) => {
                  const isSelected = selectedGoldenPath.id === gp.id;
                  return (
                    <div
                      key={gp.id}
                      onClick={() => {
                        setSelectedGoldenPath(gp);
                        setRunningGp(false);
                        setGpComplete(false);
                        setGpLogs([]);
                      }}
                      className={`bg-white border rounded-xl p-4 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-blue-600 ring-2 ring-blue-500/20 shadow-xs'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-bold text-slate-900">{gp.name}</h4>
                        <span className="text-[10px] font-mono font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                          v{gp.version}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">{gp.goal}</p>
                      <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                        <span>Adoption: <strong className="text-emerald-700">{gp.adoptionPercentage}%</strong></span>
                        <span>Avg: {gp.averageExecutionMinutes}m</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right Columns: Interactive Execution Runner */}
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-2xs space-y-6">
                <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-100">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-black text-slate-900">{selectedGoldenPath.name}</h3>
                      <span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                        {selectedGoldenPath.successRate}% Success Rate
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{selectedGoldenPath.goal}</p>
                  </div>

                  <button
                    onClick={handleRunGoldenPath}
                    disabled={runningGp}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
                  >
                    {runningGp ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    <span>{runningGp ? 'Executing...' : 'Run Golden Path'}</span>
                  </button>
                </div>

                {/* Steps List */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Execution Pipeline Steps</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedGoldenPath.steps.map((step, idx) => {
                      const isExecuted = gpComplete || (runningGp && gpStepIndex >= idx);
                      const isCurrent = runningGp && gpStepIndex === idx;
                      return (
                        <div
                          key={step.order}
                          className={`p-3 rounded-lg border text-xs transition-all ${
                            isExecuted
                              ? 'bg-emerald-50/50 border-emerald-200'
                              : isCurrent
                              ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-500/20'
                              : 'bg-slate-50 border-slate-200 opacity-60'
                          }`}
                        >
                          <div className="flex items-center justify-between font-bold text-slate-800">
                            <span className="flex items-center gap-1.5">
                              {isExecuted ? (
                                <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                              ) : (
                                <span className="w-3.5 h-3.5 rounded-full bg-slate-300 text-[10px] flex items-center justify-center text-slate-700">
                                  {step.order}
                                </span>
                              )}
                              <span>{step.title}</span>
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono">~{step.estimatedMinutes}m</span>
                          </div>
                          <p className="text-[11px] text-slate-600 mt-1">{step.description}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Guardrails Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                  <span className="font-bold text-amber-900 flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />
                    <span>Non-Negotiable Architecture Guardrails:</span>
                  </span>
                  <ul className="list-disc list-inside text-[11px] text-amber-800 space-y-0.5">
                    {selectedGoldenPath.guardrails.map((g, i) => (
                      <li key={i}>{g}</li>
                    ))}
                  </ul>
                </div>

                {/* Live Console Output */}
                <div>
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Terminal className="w-3.5 h-3.5" />
                    <span>Pipeline Output & Artifact Generation</span>
                  </h4>
                  <div className="bg-slate-950 text-slate-200 font-mono text-[11px] p-4 rounded-xl max-h-48 overflow-y-auto space-y-1">
                    {gpLogs.length === 0 ? (
                      <div className="text-slate-500 italic">Click &quot;Run Golden Path&quot; to execute automated scaffolding...</div>
                    ) : (
                      gpLogs.map((log, idx) => (
                        <div key={idx} className={log.includes('✅') ? 'text-emerald-400 font-bold' : ''}>
                          {log}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: SELF-SERVICE CATALOG */}
        {activeTab === 'self-service' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-2xs">
              <div className="max-w-2xl">
                <h2 className="text-base font-black text-slate-900">Platform Self-Service Datastores & Queues</h2>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Provision isolated Redis namespaces, PostgreSQL domain schemas, and BullMQ worker queues instantly without opening manual ops tickets.
                </p>
              </div>

              {reqSuccessMsg && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span>{reqSuccessMsg}</span>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6">
                {/* Offering Selection */}
                <div className="lg:col-span-5 space-y-3">
                  <span className="text-xs font-bold text-slate-500 uppercase">Available Self-Service Offerings</span>
                  {MOCK_PLATFORM_OFFERINGS.map((offering) => {
                    const isSelected = selectedOfferingId === offering.id;
                    return (
                      <div
                        key={offering.id}
                        onClick={() => setSelectedOfferingId(offering.id)}
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50/30 ring-2 ring-blue-500/20'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-slate-900">{offering.name}</h4>
                          <span className="text-[10px] font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                            ${offering.costImpactUsdPerMonth}/mo
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{offering.description}</p>
                        <div className="mt-2 text-[10px] text-slate-500 font-mono">
                          SLA: ~{offering.provisioningSlaMinutes} min {offering.approvalRequired && '• Requires SRE Review'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Provisioning Form */}
                <div className="lg:col-span-7 bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-bold text-slate-900 pb-3 border-b border-slate-200">
                    Configure & Dry-Run Provisioning
                  </h3>

                  <form onSubmit={handleSubmitSelfService} className="space-y-4 mt-4 text-xs">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Target Environment</label>
                      <select className="w-full bg-white border border-slate-200 rounded-lg p-2 text-slate-800">
                        <option>DEVELOPMENT (Instant Self-Service)</option>
                        <option>STAGING (Preview Environment)</option>
                        <option>PRODUCTION (Guardrailed)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Namespace / Key Prefix</label>
                      <input
                        type="text"
                        value={offeringInputs.namespaceName}
                        onChange={(e) => setOfferingInputs({ ...offeringInputs, namespaceName: e.target.value })}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 font-mono"
                      />
                      <span className="text-[10px] text-slate-500">Must end with a colon (e.g. fx_rate_cache:)</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block font-bold text-slate-700 mb-1">Default TTL (Seconds)</label>
                        <input
                          type="number"
                          value={offeringInputs.defaultTtlSeconds}
                          onChange={(e) => setOfferingInputs({ ...offeringInputs, defaultTtlSeconds: Number(e.target.value) })}
                          className="w-full bg-white border border-slate-200 rounded-lg p-2 font-mono"
                        />
                      </div>
                      <div>
                        <label className="block font-bold text-slate-700 mb-1">Memory Budget (MB)</label>
                        <input
                          type="number"
                          value={offeringInputs.memoryBudgetMb}
                          onChange={(e) => setOfferingInputs({ ...offeringInputs, memoryBudgetMb: Number(e.target.value) })}
                          className="w-full bg-white border border-slate-200 rounded-lg p-2 font-mono"
                        />
                      </div>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={submittingReq}
                        className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors cursor-pointer shadow-2xs flex items-center justify-center gap-2"
                      >
                        {submittingReq ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
                        <span>{submittingReq ? 'Provisioning Resource...' : 'Submit Provisioning Request'}</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Recent Provisioning Requests Table */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs space-y-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase">Recent Provisioning Requests</h3>
              <div className="divide-y divide-slate-100 text-xs">
                {MOCK_PROVISIONING_REQUESTS.map((req) => (
                  <div key={req.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 font-bold text-slate-800">
                        <span className="font-mono text-slate-500">{req.id}</span>
                        <span>{req.offeringName}</span>
                        <span className="text-[10px] font-mono bg-slate-100 px-1.5 py-0.2 rounded text-slate-600">
                          {req.environment}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        Requested by {req.requestedBy} ({req.requestingTeam}) at {req.createdAt}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        req.state === 'SUCCEEDED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {req.state}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: BLAST RADIUS & IMPACT ANALYSIS */}
        {activeTab === 'blast-radius' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-2xs">
              <div className="max-w-2xl">
                <h2 className="text-base font-black text-slate-900">Architecture Blast Radius & Impact Analyzer</h2>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Before introducing a breaking schema, API, or library change, compute the transitive dependent blast radius across all YamooPay customer-facing calculators and partner APIs.
                </p>
              </div>

              {/* Selector Bar */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="md:col-span-5">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Target Component / Service</label>
                  <select
                    value={impactTargetId}
                    onChange={(e) => setImpactTargetId(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-medium"
                  >
                    {MOCK_CATALOG_ENTITIES.map((e) => (
                      <option key={e.entityId} value={e.entityId}>
                        {e.name} ({e.criticality})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-5">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Proposed Change Type</label>
                  <select
                    value={impactChangeType}
                    onChange={(e) => setImpactChangeType(e.target.value as any)}
                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-medium"
                  >
                    <option value="DATABASE_SCHEMA">Database Schema Migration</option>
                    <option value="API_CONTRACT">Public / Internal API Contract Change</option>
                    <option value="EVENT_SCHEMA">Domain Event Schema Update</option>
                    <option value="RULESET_UPDATE">Financial Statutory Tax Ruleset Update</option>
                    <option value="INFRASTRUCTURE">VPS Docker Container / Dokploy Upgrade</option>
                  </select>
                </div>

                <div className="md:col-span-2 flex items-end">
                  <button
                    onClick={handleAnalyzeImpact}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-1.5 shadow-2xs"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    <span>Analyze</span>
                  </button>
                </div>
              </div>

              {/* Impact Analysis Result Card */}
              <div className="mt-6 border border-slate-200 rounded-xl p-6 bg-white space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">Calculated Blast Radius Score:</span>
                    <span
                      className={`text-xs font-black px-2.5 py-0.5 rounded-full ${
                        impactResult.blastRadiusScore === 'HIGH'
                          ? 'bg-red-100 text-red-800 border border-red-200'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {impactResult.blastRadiusScore} RISK
                    </span>
                  </div>
                  <span className="text-xs text-slate-500 font-mono">
                    Target: {impactResult.targetEntityId}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="font-bold text-slate-700 block mb-1">Direct Consumers ({impactResult.directConsumers.length})</span>
                    <ul className="list-disc list-inside text-slate-600 space-y-0.5">
                      {impactResult.directConsumers.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="font-bold text-slate-700 block mb-1">Transitive Consumers</span>
                    <ul className="list-disc list-inside text-slate-600 space-y-0.5">
                      {impactResult.transitiveConsumers.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="font-bold text-slate-700 block mb-1">Accountable Owners</span>
                    <ul className="list-disc list-inside text-slate-600 space-y-0.5">
                      {impactResult.accountableOwners.map((o, i) => (
                        <li key={i}>{o}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Suggested Tests & Rollback Strategy */}
                <div className="p-4 bg-blue-50/60 border border-blue-200 rounded-xl space-y-2 text-xs">
                  <div className="font-bold text-blue-950 flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-blue-700" />
                    <span>Required Automated Verification Suite Before Merge:</span>
                  </div>
                  <div className="font-mono text-[11px] bg-slate-900 text-slate-100 p-3 rounded-lg space-y-1">
                    {impactResult.suggestedAutomatedTests.map((t, i) => (
                      <div key={i}>$ {t}</div>
                    ))}
                  </div>
                  <div className="text-[11px] text-blue-900 pt-1">
                    <strong>Safe Rollback Mechanism:</strong> {impactResult.rollbackStrategy}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: TECHNOLOGY RADAR */}
        {activeTab === 'radar' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-2xs">
              <div className="max-w-2xl">
                <h2 className="text-base font-black text-slate-900">YamooPay Official Technology Radar</h2>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Strategic technology taxonomy governing approved languages, frameworks, datastores, and infrastructure to guard against tool sprawl and maintain engineering velocity.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                {[
                  { ring: 'ADOPT', color: 'bg-emerald-50 border-emerald-200 text-emerald-900', badge: 'bg-emerald-600 text-white', desc: 'Recommended standard for all new production services.' },
                  { ring: 'TRIAL', color: 'bg-blue-50 border-blue-200 text-blue-900', badge: 'bg-blue-600 text-white', desc: 'Approved for low-risk pilots and proof-of-concepts.' },
                  { ring: 'ASSESS', color: 'bg-amber-50 border-amber-200 text-amber-900', badge: 'bg-amber-600 text-white', desc: 'Under active R&D evaluation by Architecture Team.' },
                  { ring: 'HOLD', color: 'bg-red-50 border-red-200 text-red-900', badge: 'bg-red-600 text-white', desc: 'Prohibited for new usages; migrate existing instances.' }
                ].map((r) => {
                  const itemsInRing = MOCK_TECH_RADAR.filter((i) => i.ring === r.ring);
                  return (
                    <div key={r.ring} className={`border rounded-xl p-4 ${r.color} flex flex-col justify-between`}>
                      <div>
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${r.badge}`}>
                            {r.ring}
                          </span>
                          <span className="text-xs font-mono font-bold">{itemsInRing.length} items</span>
                        </div>
                        <p className="text-[11px] mt-2 opacity-80">{r.desc}</p>
                      </div>

                      <div className="mt-4 space-y-2 pt-3 border-t border-current/10">
                        {itemsInRing.map((item) => (
                          <div key={item.id} className="bg-white/80 rounded-lg p-2 border border-black/5 text-xs">
                            <div className="font-bold text-slate-900">{item.technology}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{item.category}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* TAB 6: ENGINEERING STANDARDS */}
        {activeTab === 'standards' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-2xs">
              <div className="max-w-2xl">
                <h2 className="text-base font-black text-slate-900">YamooPay Engineering Standards & CI Enforcement</h2>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Automated architectural policies, lint rules, financial math constraints, and security standards enforced during continuous integration.
                </p>
              </div>

              <div className="divide-y divide-slate-100 mt-6 text-xs">
                {MOCK_ENGINEERING_STANDARDS.map((std) => (
                  <div key={std.id} className="py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="max-w-2xl">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-slate-500 font-bold">{std.id}</span>
                        <h4 className="font-bold text-slate-900 text-sm">{std.title}</h4>
                        <span className="text-[10px] font-bold bg-blue-50 text-blue-700 px-1.5 py-0.2 rounded">
                          {std.category}
                        </span>
                      </div>
                      <p className="text-slate-600 mt-1 leading-relaxed">{std.summary}</p>
                      <div className="text-[10px] font-mono text-slate-400 mt-1">Rule: {std.ruleReference}</div>
                    </div>

                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <div className="text-xs font-black text-emerald-700">{std.complianceRate}% Compliance</div>
                        <div className="text-[10px] text-slate-400 font-mono">{std.enforcementMechanism}</div>
                      </div>
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 7: DOCS & ONBOARDING */}
        {activeTab === 'docs' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-2xs">
              <h2 className="text-base font-black text-slate-900">Engineering Onboarding & Architecture Playbooks</h2>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Everything an engineer needs on Day 1: one-command local bootstrap, zero-trust secrets setup, and Git pull-request workflows.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 text-xs">
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                    <Terminal className="w-4 h-4" />
                  </div>
                  <h4 className="font-bold text-slate-900">1. Local Setup Bootstrap</h4>
                  <p className="text-slate-600 text-[11px]">Run `npm run dev:bootstrap` to check Node LTS, Docker daemon, and seed PostgreSQL test fixtures.</p>
                </div>

                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <Shield className="w-4 h-4" />
                  </div>
                  <h4 className="font-bold text-slate-900">2. Security & Zero-Trust</h4>
                  <p className="text-slate-600 text-[11px]">Enforce Gitleaks pre-commit hooks. Never commit `.env` credentials or private signing keys.</p>
                </div>

                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center">
                    <GitBranch className="w-4 h-4" />
                  </div>
                  <h4 className="font-bold text-slate-900">3. Release & Dokploy Gate</h4>
                  <p className="text-slate-600 text-[11px]">Merge to `main` triggers automated CI quality suites and Docker container rollout via Dokploy.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
